import { FeatureName } from '@/models/userProfile.model';

export enum AppPageNames {
  home = "Home",
  overview = "Overview",
  objections = "Objections",
  sentiment = "Sentiment",
  performance = "Performance",
  interactions = "Interactions",
  playback = "Interactions",
  playbackDetails = "Interaction",
  landing = "Landing Page",
  maintenance = "Maintenance Page",
  unknown = "Unknonwn Page",
  topics = "Topics",
  scoring = "Scoring",
  scoringScorecardDesigner = "Scoring > Create Scorecard",
  scoringScorecardResults = "Scoring > Scorecard Results",
  scoringScheduleDesigner = "Scoring > Create Schedule",
  promptStudio = "Prompt Studio"
}

export enum AppRoutePaths {
  home = '/',
  overview = '/overview',
  objections = '/objections',
  sentiment = '/sentiment',
  performance = '/performance',
  interactions = '/interactions',
  playback = '/playback',
  playbackDetails = '/interaction',
  landing = '/landing',
  maintenance = '/maintenance',
  pdfExport = "/pdfExport",
  topics = '/topics',
  scoring = '/scoring',
  scoringScorecardDesigner = '/scoring/scorecards/designer',
  scoringScorecardResults = '/scoring/scorecards/results',
  scoringScheduleDesigner = '/scoring/schedules/designer',
  promptStudio = '/prompt-studio',
  // These four were REFERENCED BY COMPONENTS BUT NEVER DEFINED, across 11 call
  // sites in components/autoQA/** and components/scorecards/**. A missing enum
  // member evaluates to `undefined`, so every one of those navigations went to
  // `/undefined` or `undefined/<id>` rather than to a page — e.g.
  // `router.push(`${AppRoutePaths.autoQAScorecardDesigner}/${scorecardId}`)`
  // produced "undefined/sc-99".
  //
  // Nothing caught it: there is no typecheck script in package.json (only
  // `nuxt build`), and the unit tests that did catch it were failing and being
  // treated as a known-bad suite. Each path below matches an existing page:
  //   pages/auto-qa/index.vue
  //   pages/auto-qa/schedules/designer/[[id]]/index.vue
  //   pages/auto-qa/scorecards/designer/[[id]]/index.vue
  //   pages/scorecards/designer/[[id]]/index.vue
  autoQA = '/auto-qa',
  autoQAScheduleDesigner = '/auto-qa/schedules/designer',
  autoQAScorecardDesigner = '/auto-qa/scorecards/designer',
  scorecardDesigner = '/scorecards/designer',
}

export enum AppRouteNames {
  home = "index",
  overview = "overview",
  objections = "objections",
  sentiment = "sentiment",
  performance = "performance",
  interactions = "interactions",
  playback = "playback",
  playbackDetails = "interaction-id",
  landing = "landing",
  maintenance = "maintenance",
  pdfExport = "pdfExport",
  topics = "topics",
  scoring = "scoring",
  scorecards = "scorecards",
  scoringScorecardDesigner = "scoring-scorecards-designer-id",
  scoringScorecardResults = "scoring-scorecards-results-id",
  scoringScheduleDesigner = "scoring-schedules-designer-id",
  promptStudio = "prompt-studio",
}

export const ROUTES_CONFIG = [
  {
    name: AppRouteNames.home,
    pageName: AppPageNames.home,
    pageIconCssClass: "icon-home-filled",
    featureName: FeatureName.homePage,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.overview,
    pageName: AppPageNames.overview,
    pageIconCssClass: undefined,
    featureName: undefined,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.objections,
    pageName: AppPageNames.objections,
    pageIconCssClass: undefined,
    featureName: FeatureName.objectionsTab,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.topics,
    pageName: AppPageNames.topics,
    pageIconCssClass: undefined,
    featureName: undefined,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.sentiment,
    pageName: AppPageNames.sentiment,
    pageIconCssClass: undefined,
    featureName: FeatureName.sentimentTab,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.performance,
    pageName: AppPageNames.performance,
    pageIconCssClass: undefined,
    featureName: FeatureName.performanceTab,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.interactions,
    pageName: AppPageNames.interactions,
    pageIconCssClass: undefined,
    featureName: undefined,
    isViewMode: true,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.playback,
    pageName: AppPageNames.playback,
    pageIconCssClass: "icon-play-outline",
    featureName: undefined,
    isViewMode: false,
    hasTopbarFilter: true
  },
  {
    name: AppRouteNames.playbackDetails,
    pageName: AppPageNames.playbackDetails,
    pageIconCssClass: "icon-play-outline",
    featureName: undefined,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.landing,
    pageName: AppPageNames.landing,
    pageIconCssClass: undefined,
    featureName: undefined,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.maintenance,
    pageName: AppPageNames.maintenance,
    pageIconCssClass: undefined,
    featureName: undefined,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.scoring,
    pageName: AppPageNames.scoring,
    pageIconCssClass: "icon-list-check",
    featureName: FeatureName.scoringScorecardsPage,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.scoringScorecardDesigner,
    pageName: AppPageNames.scoringScorecardDesigner,
    pageIconCssClass: "icon-list-check",
    featureName: FeatureName.scoringScorecardsPage,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.scoringScorecardResults,
    pageName: AppPageNames.scoringScorecardResults,
    pageIconCssClass: "icon-list-check",
    featureName: FeatureName.questionLevelReporting,
    isViewMode: false,
    // The filter bar sits in the page content rather than the top bar, but a copied link still
    // carries its filters and date range (McSharedLink).
    hasTopbarFilter: true
  },
    {
    name: AppRouteNames.scoringScheduleDesigner,
    pageName: AppPageNames.scoringScheduleDesigner,
    pageIconCssClass: "icon-list-check",
    featureName: FeatureName.scoringSchedulesPage,
    isViewMode: false,
    hasTopbarFilter: false
  },
  {
    name: AppRouteNames.promptStudio,
    pageName: AppPageNames.promptStudio,
    pageIconCssClass: "icon-books-outline",
    featureName: FeatureName.promptStudioPage,
    isViewMode: false,
    hasTopbarFilter: false
  },
];